<template>
  <div class="date-picker__wrapper">
    <div
      class="date-picker"
      :class="{
        showDatePlaceholder: date !== '',
        showTimePlaceholder: time !== '',
        error,
      }"
    >
      <div
        class="input-wrapper"
        v-if="type !== 'time'"
        :class="{
          active: date !== '' || dateFocused || true,
        }"
      >
        <label for="date">{{ datePlaceholder }}</label>
        <input
          id="date"
          placeholder="mm-dd-yyyy"
          type="date"
          :value="date"
          :min="minDate"
          :max="maxDate"
          :disabled="dateDisabled"
          @blur="$emit('blur', $event)"
          @focusin="dateFocused = true"
          @focusout="dateFocused = false"
          @input="
            event => {
              $emit('update:date', (event.target as HTMLInputElement).value);
              $emit('change', event);
            }
          "
        />
      </div>
      <div class="vl" v-if="!(type === 'date' || type === 'time')"></div>

      <div
        class="input-wrapper"
        v-if="type !== 'date'"
        :class="{
          active: time !== '' || timeFocused || true,
        }"
      >
        <label for="time">{{ timePlaceholder }}</label>
        <input
          id="time"
          :value="time"
          type="time"
          :min="minTime"
          :max="maxTime"
          :disabled="timeDisabled"
          @blur="$emit('blur', $event)"
          @focusin="timeFocused = true"
          @focusout="timeFocused = false"
          @input="
            event => {
              $emit('update:time', (event.target as HTMLInputElement).value);
              $emit('change', event);
            }
          "
          :placeholder="timePlaceholder"
        />
      </div>
    </div>

    <!-- Error message -->

    <transition name="scale-fade">
      <div v-if="error && !inlineError" class="smaller communication-danger date-picker-field__error">
        <span>{{ error }}</span>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
  import { ref } from 'vue';

  export interface Props {
    type?: 'date' | 'time' | 'datetime';
    time?: string;
    minTime?: string;
    maxTime?: string;
    date?: string;
    minDate?: string;
    maxDate?: string;
    datePlaceholder?: string;
    timePlaceholder?: string;
    error?: string | null;
    dateDisabled?: boolean;
    timeDisabled?: boolean;
    inlineError?: boolean;
  }

  withDefaults(defineProps<Props>(), {
    type: 'datetime',
    time: '',
    date: '',
    datePlaceholder: 'Date',
    timePlaceholder: 'Time',
  });

  const emit = defineEmits(['update:date', 'update:time', 'blur', 'change']);

  const dateFocused = ref(false);
  const timeFocused = ref(false);
</script>

<style lang="scss" scoped>
  @import '@/assets/styles/variables.scss';

  .date-picker__wrapper {
    width: 100%;
    .date-picker-field__error {
      padding: 0.5rem 1.5rem;
      width: 100%;
    }
  }

  .date-picker {
    background-color: $white;
    display: flex;
    align-items: center;
    border-radius: 0.5rem;
    outline: 1px solid $input-border;
    height: 3.5rem;
    width: 100%;
    overflow: hidden;

    * {
      transition: all ease 0.2s;
      cursor: pointer;
    }

    &.error {
      outline: 1px solid $communication-danger;
    }

    .vl {
      padding-left: 1rem;
    }

    .input-wrapper {
      position: relative;
      height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 0rem 1.5rem;

      label {
        position: absolute;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        top: 0;
        font-size: 0.75rem;
        color: $input-placeholder;
      }
      &.active {
        input {
          opacity: 1;
          padding-top: 0.75rem;
        }
        label {
          height: 1.5rem;
        }
      }

      input {
        opacity: 0;
        width: 100%;
        border: none;
        padding: 0.25rem 0.5rem 0 0;

        &:focus {
          outline: none;
          cursor: pointer;
        }
        &::placeholder {
          color: $neutral-black;
        }
        &.showPlaceholder {
          display: none;
        }
        &:disabled {
          cursor: not-allowed;
        }
      }

      &:has(input:disabled) {
        cursor: not-allowed;
        color: $neutral-gray;
        background-color: $bg-light;
      }
    }
  }
</style>
